/**
 * @typedef {ReturnType<typeof import('widgets/forms/BasicInput').default>} BasicInput
 * @typedef {InstanceType<ReturnType<typeof import('widgets/forms/BasicInput').default>>} basicInput
 * @typedef {InstanceType<typeof import('widgets/toolbox/RefElement').RefElement>} refElement
 */

const regexToCardsMaping = {
    Bijcard: /^5100081/, // CLARIFY: full length of card ?
    Jcb: /^(?:2131|1800|35\d{3})\d{11}$/,
    Cup: /^(62[0-9]{14,17})$/,
    Visa: /^4[0-9]{12}(?:[0-9]{3})?$/,
    Maestro: /^(5018|5020|5038|6304|6759|6761|6763)[0-9]{8,15}$/,
    Master: /^(?:5[1-5][0-9]{2}|222[1-9]|22[3-9][0-9]|2[3-6][0-9]{2}|27[01][0-9]|2720)[0-9]{12}$/,
    Amex: /^3[47][0-9]{13}$/,
    Diners: /^3(?:0[0-5]|[68][0-9])[0-9]{11}$/,
    // eslint-disable-next-line max-len
    Discover: /^65[4-9][0-9]{13}|64[4-9][0-9]{13}|6011[0-9]{12}|(622(?:12[6-9]|1[3-9][0-9]|[2-8][0-9][0-9]|9[01][0-9]|92[0-5])[0-9]{10})$/,
    Cartebancaire: /^(4|5|6)\d{15}/
};

/**
 * @param {ReturnType<typeof import('widgets/checkout/PaymentAccordionItem').default>} PaymentAccordionItem Base widget for extending
 * @returns {typeof PaymentAccordionItemCREDITCARD} Credit Card Payment item widget
 */
export default function (PaymentAccordionItem) {
    /**
     * @category widgets
     * @subcategory checkout
     * @class PaymentAccordionItemCREDITCARD
     * @augments PaymentAccordionItem
     * @classdesc represents PaymentAccordionItemCREDITCARD component the with next features:
     * 1. Handles payment tab submit
     * 2. Validates payment tab child elements
     *
     * @example <caption>Example of PaymentAccordionItemPAYPALEXPRESS widget usage</caption>
     *  <section
     *      data-widget="paymentAccordionItem${paymentOption.ID}"
     *      data-widget-event-closeallitems="closeItems"
     *      data-method-id="${paymentOption.ID}"
     *      data-method-name="${paymentOption.name}"
     *      class="b-payment_accordion-item"
     *  >
     *      <isinclude template="${'checkout/billing/paymentOptions/paymentTitle_' + paymentOption.ID}" />
     *      <div
     *          id="payment-details-${paymentOption.ID}"
     *          data-ref="accordionItemPanel"
     *          class="b-payment_accordion-content"
     *      >
     *          <div class="b-payment_accordion-content_inner" data-ref="accordionItemPanelInner">
     *              <isinclude template="${'checkout/billing/paymentOptions/paymentBody_' + paymentOption.ID}" />
     *          </div>
     *      </div>
     *  </section>
     */
    class PaymentAccordionItemCREDITCARD extends PaymentAccordionItem {
        prefs() {
            return {
                creditCard: {
                    type: 'dwfrm_billing_creditCardFields_cardType',
                    number: 'dwfrm_billing_creditCardFields_cardNumber',
                    expirationMonth: 'dwfrm_billing_creditCardFields_expirationMonth',
                    expirationYear: 'dwfrm_billing_creditCardFields_expirationYear',
                    save: 'dwfrm_billing_creditCardFields_saveCard',
                    securityCode: 'dwfrm_billing_creditCardFields_securityCode'
                },
                sensitiveFields: ['number', 'expirationMonth', 'expirationYear', 'securityCode'],
                savedCard: {
                    selectedCardID: 'dwfrm_billing_creditCardFields_selectedCardID',
                    securityCode: 'securityCode'
                },
                newPaymentBlock: 'newPaymentBlock',
                storedPaymentInstrumentsBlock: 'storedPaymentInstrumentsBlock',
                ...super.prefs()
            };
        }

        /**
         * @description Handles payment tab submit
         * @returns {Promise} result
         */
        process() {
            return new Promise(resolve => {
                const fields = this.getFormFields();

                if (!this.ref('newPaymentBlock').hasAttr('hidden')) {
                    fields[this.prefs().creditCard.type] = this.determinCardType(
                        fields[this.prefs().creditCard.number]
                    ).shift() || 'Visa';

                    if (fields[this.prefs().creditCard.number]) {
                        fields[this.prefs().creditCard.number] = fields[this.prefs().creditCard.number].replace(/[\s-]/g, '');
                    }
                }

                resolve(fields);
            });
        }

        /**
         * @description Validates credit card payment tab child elements.
         * @returns {boolean} validation result
         */
        isChildrenValid() {
            var valid = true;

            if (this.ref('newPaymentBlock').hasAttr('hidden')) {
                return this.validateSavedCard();
            }

            const BasicInput = /** @type {BasicInput} */(this.getConstructor('basicInput'));

            this.eachChild(item => {
                if (item.getName() && !this.isCreditCardField(item.getName())) {
                    return;
                }

                if (item instanceof BasicInput && typeof item.validate === 'function' && !item.validate()) {
                    if (valid && item.setFocus) {
                        item.setFocus();
                    }
                    valid = false;
                }
            });

            return valid;
        }

        /**
         * @description Returns credit card form field id\value map
         * @returns {object} result
         */
        getFormFields() {
            /**
             * @type {{[x: string]: string}}
             */
            var fields = {};

            if (this.ref('newPaymentBlock').hasAttr('hidden')) {
                return this.getSavedCardFields();
            }

            const BasicInput = /** @type {BasicInput} */(this.getConstructor('basicInput'));

            this.eachChild(widget => {
                if (widget instanceof BasicInput && !(widget.skipSubmission && widget.skipSubmission())) {
                    const name = widget.getName && widget.getName();

                    if (name && !this.isSavedCardField(name)) {
                        fields[name] = widget.getValue();
                    }
                }
            });

            return fields;
        }

        /**
         * @description Determin card type by card number
         * @param {string} cardNumber number of card
         * @returns {Array<string>} result
         */
        determinCardType(cardNumber) {
            const cardNumberReplaced = cardNumber.replace(/[\s-]/g, '');

            return Object.keys(regexToCardsMaping)
                .filter(key => regexToCardsMaping[key] && regexToCardsMaping[key].test(cardNumberReplaced));
        }

        /**
         * @description Checks if field is saved credit card field
         * @param {string} id - field id
         * @returns {boolean} result
         */
        isSavedCardField(id) {
            return [this.prefs().savedCard.selectedCardID, this.prefs().savedCard.securityCode].includes(id);
        }

        /**
         * @description Returns saved credit card form field id\value map
         * @returns {object} result
         */
        getSavedCardFields() {
            var fields = {};

            this.eachChild(widget => {
                const name = widget.getName && widget.getName();

                if (widget.id === this.prefs().savedCard.selectedCardID && name) {
                    fields[name] = widget.getValue();
                } else if (
                    widget.id === this.prefs().savedCard.securityCode
                    && widget.ref('self').data('selected')
                    && name
                ) {
                    fields[name] = widget.getValue();
                }
            });

            return fields;
        }

        /**
         * @description Validates saved credit card
         * @returns {boolean} result
         */
        validateSavedCard() {
            let valid = true;

            this.eachChild(item => {
                var isCurrentCVVField = (item.getName() === this.prefs().savedCard.securityCode)
                    && item.ref('self').data('selected');

                if (isCurrentCVVField && typeof item.validate === 'function' && !item.validate()) {
                    item.setFocus();

                    valid = false;
                }
            });

            return valid;
        }

        /**
         * @description Handles add new card click
         * @listens dom:click
         * @returns {void}
         */
        addNewCard() {
            this.ref('storedPaymentInstrumentsBlock').hide();
            this.showNewCreditCard();

            this.updateState();
        }

        /**
         * @description Clears saved card sensetive data
         * @returns {void}
         */
        clearSavedCardSensitiveData() {
            this.eachChild(child => {
                if (child.id === this.prefs().savedCard.securityCode) {
                    child.setValue('', true);
                    child.clearError();
                }
            });
        }

        /**
         * @description Handles show saved payments click
         * @listens dom:click
         * @returns {void}
         */
        showSavedPayments() {
            this.clearSavedCardSensitiveData();

            this.ref('storedPaymentInstrumentsBlock').show();
            this.ref('newPaymentBlock').hide();

            this.eachChild(child => {
                if (child.id === this.prefs().savedCard.securityCode) {
                    child.setValue('', true);
                }
            });

            this.updateState();
            // class AccessibilityFocusMixin, method focusFirst
            this.callIfExists('focusFirst', 'storedPaymentInstrumentsBlock', ['input:checked']);
        }

        /**
         * @description Handles show new credit card form click
         * @listens dom:click
         * @returns {void}
         */
        showNewCreditCard() {
            this.eachChild((/** @type {basicInput} */child) => {
                if (this.isCreditCardField(child.id)) {
                    child.setValue('', true);
                    child.clearError();
                }
            });
            this.ref('newPaymentBlock').show();
            // class AccessibilityFocusMixin, method focusFirstInput
            this.callIfExists('focusFirstInput', 'newPaymentBlock');
        }

        /**
         * @description Checks if field is credit card field
         * @param {string} id - field id
         * @returns {boolean} result
         */
        isCreditCardField(id) {
            var status = false;

            Object.keys(this.prefs().creditCard).forEach(field => {
                this.getById(this.prefs().creditCard[field], (/** @type {basicInput} */el) => {
                    if (el && el.id === id) {
                        status = true;
                    }
                });
            });

            return status;
        }

        /**
         * @description Handles select saved payment
         * @listens dom:click
         * @param {refElement} element - saved payment
         * @returns {void}
         */
        selectSavedPayment(element) {
            this.getById(this.prefs().savedCard.selectedCardID, (/** @type {basicInput} */savedCardID) => {
                savedCardID.setValue(element.data('uuid'), true);
            });

            this.eachChild(child => {
                if (child.getName() === this.prefs().savedCard.securityCode) {
                    const isCurrentCard = child.ref('self').data('uuid') === element.data('uuid');

                    child.ref('self').data('selected', isCurrentCard);

                    if (!isCurrentCard) {
                        child.setValue('', true);
                        child.clearError();
                    }
                }
            });
        }

        /**
         * @description Clears sensetive data
         * @returns {void}
         */
        cleanupSensitiveData() {
            const sensitiveFields = this.prefs().sensitiveFields.map(field => this.prefs().creditCard[field]);
            this.eachChild(child => {
                if (sensitiveFields.includes(child.id)) {
                    child.setValue('', true);
                }
            });
        }
    }

    return PaymentAccordionItemCREDITCARD;
}
